import { BaseService } from '@/infrastructure/api/base.service'
import type {
  ActionRedirectResponse,
  CancelSubscriptionResponse,
  CheckAccountActivationResponse,
  CompanyCountry,
  GetInvoicesResponse,
  InvitableUser,
  LoadPlanChangeDetailsCommand,
  PlanChangeCommand,
  PlanChangeDetailsResponse,
  PlanInfo,
  PurchasePlanResponse,
  UpdateCompanyDetailsCommand,
} from '@/features/subscription/types/subscription.types'

/** PascalCase row shape returned by /Subscription/GetInvitableUsers. */
interface InvitableUserResponse {
  Id: string
  Email: string
  FullName: string
  Active: boolean
  IsCurrentUser: boolean
}

/** PascalCase `userManagement` shape returned by /V2/AccountManagement/GetUsers. */
interface UserManagementPlanResponse {
  Plan?: string
  PlanPrice?: string
  PlanBillingType?: string
  PlanIsAnnual?: boolean
  PlanCurrency?: string
  PlanIsActive?: boolean
  Credits?: number
  MobileCredits?: number
  UnrestrictedCredits?: number
  UnrestrictedMobileCredits?: number
  Company?: string
  CompanyWebsite?: string
  CountryDetail?: { Id?: number; Name?: string }
}

export class SubscriptionService extends BaseService {
  constructor() {
    super('')
  }

  async activatePlan(selectedUserIds?: string[]): Promise<ActionRedirectResponse> {
    // FromV3 tells the backend to use the V3 Stripe cancel URL (/subscription/alert)
    // instead of the Vue 2 /V2/SubscriptionAlert page when the user backs out.
    return this.post<ActionRedirectResponse>('/Subscription/ActivatePlan', {
      SelectedUserIds: selectedUserIds ?? [],
      FromV3: true,
    })
  }

  /** Team members who can be given a paid seat, mapped to camelCase. */
  async getInvitableUsers(): Promise<InvitableUser[]> {
    const data = await this.get<{ result: boolean; users: InvitableUserResponse[] }>(
      '/Subscription/GetInvitableUsers',
    )
    return (data.users ?? []).map((u) => ({
      id: u.Id,
      email: u.Email,
      fullName: u.FullName,
      active: u.Active,
      isCurrentUser: u.IsCurrentUser,
    }))
  }

  async purchasePlan(command: PlanChangeCommand): Promise<PurchasePlanResponse> {
    return this.post<PurchasePlanResponse>('/Subscription/PurchasePlan', { ...command, FromV3: true })
  }

  async loadPlanChangeDetails(
    command: LoadPlanChangeDetailsCommand,
  ): Promise<PlanChangeDetailsResponse> {
    return this.post<PlanChangeDetailsResponse>('/Subscription/LoadPlanChangeDetails', command)
  }

  async payFailedSubscription(): Promise<ActionRedirectResponse> {
    return this.post<ActionRedirectResponse>('/Subscription/PayFailedSubscription')
  }

  async updatePaymentMethod(): Promise<ActionRedirectResponse> {
    return this.get<ActionRedirectResponse>('/Subscription/UpdatePaymentMethod')
  }

  async cancelSubscription(): Promise<CancelSubscriptionResponse> {
    return this.post<CancelSubscriptionResponse>('/Subscription/CancelSubscription')
  }

  async getInvoices(): Promise<GetInvoicesResponse> {
    return this.get<GetInvoicesResponse>('/Subscription/GetInvoices')
  }

  async checkAccountActivation(email: string): Promise<CheckAccountActivationResponse> {
    return this.post<CheckAccountActivationResponse>('/V2/CheckAccountActivation', { email })
  }

  /**
   * Current-plan financials (credits, price, billing) — parity with Vue 2, which
   * reads these from GetUsers, NOT from GetSubscriptionDetails. Maps the PascalCase
   * `userManagement` payload to the camelCase `PlanInfo` the UI binds.
   */
  async getPlanInfo(): Promise<PlanInfo> {
    const data = await this.get<{ result: boolean; userManagement: UserManagementPlanResponse }>(
      '/V2/AccountManagement/GetUsers',
    )
    const um = data.userManagement ?? {}
    return {
      planName: um.Plan ?? '',
      planPrice: um.PlanPrice ?? '',
      planBillingType: um.PlanBillingType ?? '',
      planIsAnnual: um.PlanIsAnnual ?? false,
      planCurrency: um.PlanCurrency ?? '',
      planIsActive: um.PlanIsActive ?? false,
      exportCredits: um.Credits ?? 0,
      mobileCredits: um.MobileCredits ?? 0,
      unrestrictedExportCredits: um.UnrestrictedCredits ?? 0,
      unrestrictedMobileCredits: um.UnrestrictedMobileCredits ?? 0,
      companyName: um.Company ?? '',
      companyWebsite: um.CompanyWebsite ?? '',
      companyCountryId: um.CountryDetail?.Id ?? 0,
      companyCountryName: um.CountryDetail?.Name ?? '',
    }
  }

  /**
   * Asks the sales team to get in touch about upgrading (Vue 2 accountManagementService
   * `sendEmailSalesTeam`). The backend emails them on behalf of the current user.
   */
  async sendUpgradeRequestToSales(): Promise<void> {
    await this.post('/V2/AccountManagement/SendEmailSalesTeam')
  }

  /**
   * Asks the sales team to get in touch about downgrading from organic Enterprise (Vue 2
   * accountManagementService `sendDowngradeEmailSalesTeam`).
   */
  async sendDowngradeRequestToSales(): Promise<void> {
    await this.post('/V2/AccountManagement/SendDowngradeEmailSalesTeam')
  }

  /**
   * Countries for the company-details country picker (`{ Id, Name }`). Uses the same
   * proven endpoint as the user-details feature (LoadCountries returns the full list).
   */
  async getCountries(): Promise<CompanyCountry[]> {
    const data = await this.get<{ countries: CompanyCountry[] }>(
      '/V2/AccountManagement/LoadCountries',
    )
    return data.countries ?? []
  }

  /** Persist edited company details. Returns whether the save succeeded. */
  async saveCompanyDetails(command: UpdateCompanyDetailsCommand): Promise<boolean> {
    const data = await this.post<{ success: boolean }>(
      '/V2/AccountManagement/SaveCompanyDetails',
      command,
    )
    return data?.success ?? false
  }
}
